import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '@/contexts/LanguageContext';
import CyberLayout from '@/components/CyberLayout';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Lock, ArrowRight, Loader2, Shield } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const translations = {
  it: {
    title: 'Codice di Accesso',
    subtitle: 'Inserisci il codice fornito dall\'agenzia per accedere al questionario',
    placeholder: 'Inserisci codice...',
    continue: 'Accedi',
    invalidCode: 'Codice non valido',
    expiredCode: 'Codice scaduto',
    usedCode: 'Codice già utilizzato',
    error: 'Errore durante la verifica',
    noCode: 'Non hai un codice? Contatta l\'agenzia.',
    secureAccess: 'Accesso Sicuro'
  },
  en: {
    title: 'Access Code',
    subtitle: 'Enter the code provided by the agency to access the questionnaire',
    placeholder: 'Enter code...',
    continue: 'Access',
    invalidCode: 'Invalid code',
    expiredCode: 'Code expired',
    usedCode: 'Code already used',
    error: 'Error during verification',
    noCode: 'Don\'t have a code? Contact the agency.',
    secureAccess: 'Secure Access'
  }
};

export default function AccessCode() {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const t = translations[language];
  
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!code.trim()) {
      setError(t.invalidCode);
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post(`${API}/verify-access-code`, {
        code: code.trim().toUpperCase()
      });
      
      if (response.data.valid) {
        // Store access info in sessionStorage
        sessionStorage.setItem('accessCode', code.trim().toUpperCase());
        sessionStorage.setItem('accessCodeType', response.data.code_type);
        if (response.data.email) {
          sessionStorage.setItem('clientEmail', response.data.email);
        }
        if (response.data.client_name) {
          sessionStorage.setItem('clientName', response.data.client_name);
        }
        
        toast.success(response.data.message);
        navigate('/');
      }
    } catch (err) {
      const detail = err.response?.data?.detail || t.error;
      if (detail.includes('scaduto') || detail.includes('expired')) {
        setError(t.expiredCode);
      } else if (detail.includes('utilizzato') || detail.includes('used')) {
        setError(t.usedCode);
      } else {
        setError(t.invalidCode);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (e) => {
    // Convert to uppercase and limit to 10 characters
    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10);
    setCode(value);
    setError('');
  };

  return (
    <CyberLayout>
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <img 
              src="/logo-smart-info.png" 
              alt="Smart Info" 
              className="h-20 mx-auto mb-4"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </div>

          {/* Access Card */}
          <div className="cyber-card rounded-xl p-8">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-neon-green/10 mb-4">
                <Shield className="w-8 h-8 text-neon-green" />
              </div>
              <h1 className="font-orbitron text-2xl text-white mb-2">
                {t.title}
              </h1>
              <p className="font-mono text-sm text-gray-400">
                {t.subtitle}
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <Input
                    type="text"
                    value={code}
                    onChange={handleCodeChange}
                    placeholder={t.placeholder}
                    className="cyber-input w-full h-14 pl-12 text-center text-2xl font-mono tracking-[0.5em] uppercase"
                    data-testid="access-code-input"
                    autoFocus
                    autoComplete="off"
                  />
                </div>
                
                {error && (
                  <p className="mt-2 text-sm text-red-400 font-mono text-center">
                    {error}
                  </p>
                )}
              </div>

              <Button
                type="submit"
                disabled={loading || !code.trim()}
                data-testid="access-code-submit"
                className="w-full cyber-btn h-14 font-orbitron text-lg"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    {t.continue}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>
            </form>

            {/* Help text */}
            <p className="mt-6 text-center text-xs text-gray-500 font-mono">
              {t.noCode}
            </p>
          </div>

          {/* Security badge */}
          <div className="mt-6 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10">
              <Lock className="w-4 h-4 text-neon-green" />
              <span className="font-mono text-xs text-gray-400">{t.secureAccess}</span>
            </div>
          </div>
        </div>
      </div>
    </CyberLayout>
  );
}
